import { Component } from 'react';
import { Alert, AlertTitle, Button, Box } from '@mui/material';

/**
 * ErrorBoundary - Catches React errors in child components
 *
 * Prevents blank screens by displaying error messages when components crash.
 * In development mode, shows detailed error stack trace for debugging.
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to console for debugging
    console.error('ErrorBoundary caught error:', error, errorInfo);
    this.setState({ error, errorInfo });
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    if (this.props.onReset) this.props.onReset();
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ p: 3 }}>
          <Alert severity="error">
            <AlertTitle>Something went wrong</AlertTitle>
            <Box sx={{ mt: 1, mb: 2 }}>
              There was an error displaying the results. Please try uploading the file again.
            </Box>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <Box sx={{ mt: 2, fontFamily: 'monospace', fontSize: '0.875rem' }}>
                <strong>Error:</strong> {this.state.error.toString()}
                {this.state.errorInfo && (
                  <pre style={{ whiteSpace: 'pre-wrap', marginTop: 8 }}>
                    {this.state.errorInfo.componentStack}
                  </pre>
                )}
              </Box>
            )}
          </Alert>
          <Button variant="outlined" onClick={this.handleReset} sx={{ mt: 2 }}>
            Try Again
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
